import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Upload, CheckCircle2, FileSpreadsheet, AlertTriangle, ChevronLeft, ChevronRight, Lock, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { expensasApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import { DetalleSubrubroDialog } from './DetalleSubrubroDialog';

const fmtMoney = (n) => {
  const v = Math.round(Number(n || 0));
  const sign = v < 0 ? '-' : '';
  return `${sign}$ ${Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;
// Porcentaje con formato es-AR (coma decimal) para Vista inversor / mobile.
const fmtPctV = (n) => `${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const formatPeriodo = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

/**
 * Componente embebible del detalle de un período.
 * Recibe edificioId + periodoId. Publica acciones (Importar/Recalcular/Aprobar) en `registerActions`.
 * No renderiza header propio.
 */
export default function PeriodoDetalle({ edificioId, periodoId, mes, onMesChange, registerActions, onPeriodoChanged }) {
  const { user } = useAuth();
  const puedeGestionarLiquidacion = ['admin', 'administrador_consorcio'].includes(user?.rol);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState(null);
  const [calculo, setCalculo] = useState(null);
  const [liquidacionesAprobadas, setLiquidacionesAprobadas] = useState([]);
  const [movimientos, setMovimientos] = useState(null);
  const [resumenGastos, setResumenGastos] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [liqComparativa, setLiqComparativa] = useState(null);
  const [vistaOperador, setVistaOperador] = useState(null);
  const [vistaOperadorFilter, setVistaOperadorFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [reimportConfirmOpen, setReimportConfirmOpen] = useState(false);
  const [deletingImport, setDeletingImport] = useState(false);

  const aprobado = periodo?.estado === 'aprobado';
  // Variables derivadas del cálculo (declaradas temprano para usar en accionesPorTab).
  // Regla unificada con backend (_es_sin_clasif_ignorable):
  //   - Códigos que empiezan con 1, 2 o 3 → permitidas.
  //   - Código exacto '440100' → permitido.
  //   - Cualquier otro código que empieza con 4 o 5 → requiere revisión.
  const esSinClasifIgnorable = (codigo) => {
    const c = String(codigo || '').trim();
    if (!c) return false;
    if (c === '440100') return true;
    return ['1', '2', '3'].includes(c[0]);
  };
  const sinClasifTodos = calculo?.apertura?.sin_clasificar || [];
  const sinClasifProblematicos = sinClasifTodos.filter((s) => !esSinClasifIgnorable(s.codigo));
  const nSinClasif = sinClasifTodos.length;
  const nSinClasifProblematicos = sinClasifProblematicos.length;
  const aprobarBloqueadoPorSinClasif = nSinClasifProblematicos > 0;

  const fetchPeriodo = async () => {
    const r = await expensasApi.getPeriodo(edificioId, periodoId);
    setPeriodo(r.data);
    if (onPeriodoChanged) onPeriodoChanged(r.data);
    return r.data;
  };

  const fetchCalculo = async (per) => {
    const p = per || periodo;
    if (!p?.movimientos_importados) return;
    try { const r = await expensasApi.getCalculo(edificioId, periodoId); setCalculo(r.data); }
    catch {}
  };

  const fetchLiqs = async () => {
    const r = await expensasApi.listLiquidaciones(edificioId, periodoId);
    setLiquidacionesAprobadas(r.data || []);
  };

  useEffect(() => {
    setLoading(true);
    setMovimientos(null);
    setCalculo(null);
    setLiquidacionesAprobadas([]);
    setResumenGastos(null);
    setLiqComparativa(null);
    setVistaOperador(null);
    (async () => {
      try {
        const per = await fetchPeriodo();
        if (per.movimientos_importados) {
          await fetchCalculo(per);
          // Cargar movimientos automáticamente si la pestaña activa es "mayor"
          try {
            const r = await expensasApi.getMovimientos(edificioId, periodoId);
            setMovimientos(r.data || []);
          } catch { setMovimientos([]); }
        }
        // Cargar grupos del edificio (siempre, para vista por grupos en Liquidación)
        try {
          const rp = await expensasApi.getParticipacion(edificioId);
          setGrupos(rp.data?.grupos || []);
        } catch { setGrupos([]); }
        if (per.estado === 'aprobado') await fetchLiqs();
      } finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, [edificioId, periodoId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      await expensasApi.importarColppy(edificioId, periodoId, file);
      toast.success('Mayor de Colppy importado');
      const per = await fetchPeriodo();
      await fetchCalculo(per);
      setMovimientos(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al importar'); }
    finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCalcular = async () => {
    setCalculating(true);
    try { await fetchCalculo(); toast.success('Recalculado'); }
    finally { setCalculating(false); }
  };

  const handleAprobar = async () => {
    setApproving(true);
    try {
      await expensasApi.aprobarPeriodo(edificioId, periodoId);
      toast.success('Período aprobado');
      setConfirmOpen(false);
      await fetchPeriodo();
      await fetchLiqs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setApproving(false); }
  };

  const handleRevertir = async () => {
    setReverting(true);
    try {
      const r = await expensasApi.revertirPeriodo(edificioId, periodoId);
      const { cobros_pendientes_eliminados = 0, cobros_reales_conservados = 0 } = r.data || {};
      toast.success(
        `Liquidación pasada a borrador · ${cobros_pendientes_eliminados} cobro(s) pendiente(s) eliminado(s), ${cobros_reales_conservados} pago(s) real(es) conservado(s)`
      );
      setRevertConfirmOpen(false);
      const per = await fetchPeriodo();
      await fetchCalculo(per);
      setLiquidacionesAprobadas([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al revertir'); }
    finally { setReverting(false); }
  };

  const loadMovimientos = async () => {
    if (movimientos === null) {
      try { const r = await expensasApi.getMovimientos(edificioId, periodoId); setMovimientos(r.data || []); }
      catch { setMovimientos([]); }
    }
  };

  const loadResumenGastos = async () => {
    if (resumenGastos === null) {
      try { const r = await expensasApi.getResumenGastos(edificioId, periodoId); setResumenGastos(r.data); }
      catch { setResumenGastos({ meses: [], filas: [], totales: [0, 0, 0] }); }
    }
  };

  const loadLiqComparativa = async () => {
    if (liqComparativa === null && periodo?.movimientos_importados) {
      try { const r = await expensasApi.getLiquidacionesComparativa(edificioId, periodoId); setLiqComparativa(r.data); }
      catch { setLiqComparativa({ items: [], mes_actual: { label: '' }, mes_anterior: { label: '' } }); }
    }
  };

  const loadVistaOperador = async () => {
    if (vistaOperador === null && periodo?.movimientos_importados) {
      try { const r = await expensasApi.getVistaOperador(edificioId, periodoId); setVistaOperador(r.data); }
      catch { setVistaOperador({ filas: [], totales: { expensas_mes: 0, expensas_cochera: 0, fee_administracion_consorcio: 0, total_expensa: 0 }, fee_total_consorcio: 0 }); }
    }
  };

  // Las acciones se muestran inline según la pestaña activa
  const [activeTab, setActiveTab] = usePersistentTab('subtab', 'mayor');
  const [aperturaFilter, setAperturaFilter] = useState('');

  // Dispara loaders cuando el subtab llega activo por URL (no solo por click).
  useEffect(() => {
    if (!periodo?.movimientos_importados) return;
    if (activeTab === 'resumen') loadResumenGastos();
    if (activeTab === 'liquidaciones') loadLiqComparativa();
    if (activeTab === 'vista-operador') loadVistaOperador();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, periodo?.movimientos_importados, periodoId]);

  const handleEliminarImportacion = async () => {
    const ok = window.confirm(
      `¿Eliminar la importación del Mayor de ${formatPeriodo(periodo?.periodo)}?\n\n` +
      `Se borrarán movimientos, sin clasificar, aperturas y liquidaciones de este período.\n` +
      `NO se borran unidades, propietarios, plan de cuentas, otros períodos ni otros edificios.`
    );
    if (!ok) return;
    setDeletingImport(true);
    try {
      const r = await expensasApi.eliminarImportacion(edificioId, periodoId);
      const { movimientos_eliminados, liquidaciones_eliminadas, aperturas_eliminadas } = r.data || {};
      toast.success(
        `Importación eliminada · ${movimientos_eliminados} movimientos, ${liquidaciones_eliminadas} liquidaciones, ${aperturas_eliminadas} aperturas`
      );
      // Asegurar que volvamos al subtab por defecto antes de desmontar los Tabs
      if (activeTab !== 'mayor') setActiveTab('mayor');
      const per = await fetchPeriodo();
      if (per && !per.movimientos_importados) {
        setMovimientos(null);
        setCalculo(null);
        setLiquidacionesAprobadas([]);
        setResumenGastos(null);
        setLiqComparativa(null);
        setVistaOperador(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar la importación');
    } finally {
      setDeletingImport(false);
    }
  };

  // Reimportar / Subir Colppy y Eliminar Mayor: visibles SIEMPRE que exista el
  // período, sin depender del estado (aprobado/bloqueado). Reimportar en un
  // período aprobado lo revierte a 'pendiente' en el backend.
  const handleReimportClick = () => {
    // Período aprobado: confirmar antes (reimportar lo revierte a pendiente).
    if (aprobado && periodo?.movimientos_importados) {
      setReimportConfirmOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const accionImportar = periodo && (
    <Button size="sm" variant="outline" onClick={handleReimportClick} disabled={importing} data-testid="importar-colppy-btn">
      {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
      {periodo.movimientos_importados ? 'Reimportar' : 'Subir Colppy'}
    </Button>
  );

  const accionEliminarImport = periodo?.movimientos_importados && (
    <Button
      size="sm"
      variant="outline"
      onClick={handleEliminarImportacion}
      disabled={deletingImport || importing}
      className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
      data-testid="eliminar-mayor-btn"
      title="Eliminar la importación del Mayor para este período"
    >
      {deletingImport ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
      Eliminar Mayor
    </Button>
  );

  const accionLiquidacion = periodo?.movimientos_importados && (
    <div className="flex items-center gap-2" key="liq-actions">
      <Badge
        variant={aprobado ? 'default' : 'secondary'}
        className={aprobado ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600'}
        data-testid="liq-estado-badge"
      >
        {aprobado ? (
          <><Lock className="w-3 h-3 mr-1" /> Aprobada</>
        ) : (
          'Borrador'
        )}
      </Badge>
      {!aprobado ? (
        <Button
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={!calculo || aprobarBloqueadoPorSinClasif}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="aprobar-btn"
          title={aprobarBloqueadoPorSinClasif
            ? `No se puede aprobar: ${nSinClasifProblematicos} movimiento(s) sin clasificar requieren revisión`
            : 'Aprobar período'}
        >
          <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
        </Button>
      ) : puedeGestionarLiquidacion ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRevertConfirmOpen(true)}
          disabled={reverting}
          className="text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
          data-testid="revertir-btn"
          title="Pasar la liquidación aprobada de vuelta a borrador"
        >
          {reverting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
          Pasar a borrador
        </Button>
      ) : null}
    </div>
  );

  const accionBuscarApertura = (
    <input
      type="text" placeholder="Buscar código, cuenta o rubro..."
      value={aperturaFilter}
      onChange={(e) => setAperturaFilter(e.target.value)}
      data-testid="apertura-search"
      className="text-sm h-8 px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 w-72"
    />
  );

  const accionBuscarVistaOperador = (
    <input
      type="text" placeholder="Buscar unidad o responsable..."
      value={vistaOperadorFilter}
      onChange={(e) => setVistaOperadorFilter(e.target.value)}
      data-testid="vista-operador-search"
      className="text-sm h-8 px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 w-72"
    />
  );

  const accionesPorTab = {
    mayor: (
      <div className="flex items-center gap-2" key="mayor-actions">
        {accionImportar}
        {accionEliminarImport}
      </div>
    ),
    apertura: accionBuscarApertura,
    resumen: null,
    'vista-operador': accionBuscarVistaOperador,
    liquidaciones: accionLiquidacion,
    'sin-clasif': null,
    inconsistencias: null,
  };

  // Limpiar acciones del header padre (por si quedaron de antes)
  useEffect(() => {
    if (registerActions) registerActions(null);
    return () => { if (registerActions) registerActions(null); };
  }, [registerActions]);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!periodo) return null;

  const tot = aprobado ? periodo.totales_apertura : calculo?.apertura?.totales;
  const totDist = aprobado ? periodo.totales_distribuidos : calculo?.distribucion?.totales_distribuidos;
  const apertura = calculo?.apertura?.filas || [];
  const liquidaciones = aprobado ? liquidacionesAprobadas : (calculo?.distribucion?.liquidaciones || []);
  const advertencias = calculo?.distribucion?.advertencias || [];
  const sinClasif = calculo?.apertura?.sin_clasificar || [];

  return (
    <div>
      <input type="file" accept=".xlsx,.xlsm" ref={fileInputRef} onChange={handleFileChange} className="hidden" />

      <Dialog open={reimportConfirmOpen} onOpenChange={setReimportConfirmOpen}>
        <DialogContent data-testid="reimport-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Reimportar período aprobado</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Este período ya está aprobado. Si reimportás el Mayor, el período volverá a estado
            pendiente y se descartarán los snapshots de liquidación aprobada. Esta acción puede
            modificar los importes de la liquidación.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReimportConfirmOpen(false)} data-testid="reimport-cancel-btn">
              Cancelar
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => { setReimportConfirmOpen(false); fileInputRef.current?.click(); }}
              data-testid="reimport-confirm-btn"
            >
              Reimportar de todos modos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Período + KPIs en una sola fila (siempre) */}
      <div className={`grid grid-cols-2 ${(periodo.movimientos_importados && (tot?.locales || 0) > 0) ? 'md:grid-cols-6' : 'md:grid-cols-5'} gap-2 mb-2 items-stretch`}>
        <MesSelector mes={mes} onMesChange={onMesChange} aprobado={aprobado} />
        <Kpi label="$ Expensas" value={periodo.movimientos_importados ? tot?.expensas : 0} />
        <Kpi label="$ Cocheras" value={periodo.movimientos_importados ? tot?.cocheras : 0} />
        {periodo.movimientos_importados && (tot?.locales || 0) > 0 && (
          <Kpi label="$ Locales" value={tot?.locales} />
        )}
        <Kpi label="Fondo reserva" value={periodo.movimientos_importados ? tot?.fondo_reserva : 0} />
        <Kpi label="Total" value={periodo.movimientos_importados ? tot?.total : 0} highlight />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <TabsList>
            <TabsTrigger value="mayor" data-testid="tab-mayor" onClick={loadMovimientos}>
              <span className="text-slate-400 mr-1.5 text-xs">1</span> Mayor ({periodo.movimientos_count || 0})
            </TabsTrigger>
            <TabsTrigger value="sin-clasif" data-testid="tab-sin"
                         className={nSinClasifProblematicos > 0 ? 'data-[state=active]:bg-orange-100 text-orange-700' : ''}>
              <span className="text-slate-400 mr-1.5 text-xs">2</span> Sin clasificar ({nSinClasifProblematicos})
            </TabsTrigger>
            <TabsTrigger value="apertura" data-testid="tab-apertura">
              <span className="text-slate-400 mr-1.5 text-xs">3</span> Cuentas ({apertura.length})
            </TabsTrigger>
            <TabsTrigger value="resumen" data-testid="tab-resumen" onClick={loadResumenGastos}>
              <span className="text-slate-400 mr-1.5 text-xs">4</span> Resumen
            </TabsTrigger>
            <TabsTrigger value="vista-operador" data-testid="tab-vista-operador" onClick={loadVistaOperador}>
              <span className="text-slate-400 mr-1.5 text-xs">5</span> Vista operador
            </TabsTrigger>
            <TabsTrigger value="liquidaciones" data-testid="tab-liq" onClick={loadLiqComparativa}>
              <span className="text-slate-400 mr-1.5 text-xs">6</span> Liquidación ({liqComparativa?.items?.length ?? liquidaciones.length})
            </TabsTrigger>
            <TabsTrigger value="vista-inversor" data-testid="tab-vista-inversor">
              <span className="text-slate-400 mr-1.5 text-xs">7</span> Detalle por unidad
            </TabsTrigger>
            {advertencias.length > 0 && (
              <TabsTrigger value="inconsistencias" data-testid="tab-inconsistencias"
                           className="data-[state=active]:bg-amber-100 text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                Inconsistencias
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">
                  {advertencias.length}
                </span>
              </TabsTrigger>
            )}
          </TabsList>
          <div className="flex items-center gap-2">{accionesPorTab[activeTab]}</div>
        </div>

        <TabsContent value="mayor">
          {periodo.movimientos_importados ? (
            <TablaMovimientos movimientos={movimientos} fileName={periodo.movimientos_filename} importedAt={periodo.movimientos_imported_at} />
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
              <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-700">Todavía no hay Mayor importado para este período</h3>
              <p className="text-sm text-slate-500 mt-1">Subí el XLSX del período {formatPeriodo(periodo.periodo)} para empezar.</p>
              <Button className="mt-4" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Subir archivo .xlsx
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sin-clasif">
          {periodo.movimientos_importados ? (
            <TablaSinClasificar filas={sinClasif} />
          ) : (
            <EmptyTabState label="sin-clasificar" />
          )}
        </TabsContent>

        <TabsContent value="apertura">
          {periodo.movimientos_importados ? (
            <TablaApertura filas={apertura} filter={aperturaFilter} />
          ) : (
            <EmptyTabState label="cuentas" />
          )}
        </TabsContent>

        <TabsContent value="resumen">
          {periodo.movimientos_importados ? (
            <TablaResumenGastos data={resumenGastos} edificioId={edificioId} periodoId={periodoId} aprobado={aprobado} />
          ) : (
            <EmptyTabState label="resumen" />
          )}
        </TabsContent>

        <TabsContent value="vista-operador">
          {periodo.movimientos_importados ? (
            <TablaVistaOperador
              data={vistaOperador}
              filter={vistaOperadorFilter}
              onFilterChange={setVistaOperadorFilter}
            />
          ) : (
            <EmptyTabState label="vista-operador" />
          )}
        </TabsContent>

        <TabsContent value="liquidaciones">
          {periodo.movimientos_importados ? (
            <>
              <TablaLiquidaciones comparativa={liqComparativa} />
              {totDist && (
                <div className="mt-2 text-xs text-slate-500 text-right">
                  Suma distribuida: Exp {fmtMoney(totDist.expensas)} · Coc {fmtMoney(totDist.cocheras)} · <b>{fmtMoney(totDist.total)}</b>
                </div>
              )}
            </>
          ) : (
            <EmptyTabState label="liquidaciones" />
          )}
        </TabsContent>

        <TabsContent value="vista-inversor">
          {periodo.movimientos_importados ? (
            <VistaPorUnidad edificioId={edificioId} periodoId={periodoId} />
          ) : (
            <EmptyTabState label="vista-inversor" />
          )}
        </TabsContent>

        {advertencias.length > 0 && (
          <TabsContent value="inconsistencias">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900">Inconsistencias detectadas en el cálculo</h4>
                  <p className="text-xs text-amber-800 mt-0.5">Revisá estos puntos antes de aprobar el período.</p>
                </div>
              </div>
              <ul className="space-y-2 ml-7">
                {advertencias.map((a, i) => (
                  <li key={i} className="bg-white border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-900">
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Aprobar período {formatPeriodo(periodo.periodo)}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>Al aprobar:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Se generan <b>{calculo?.distribucion?.liquidaciones?.length || 0}</b> liquidaciones por unidad.</li>
              <li>El período queda <b>inmutable</b>.</li>
              <li>Total: <b>{fmtMoney(tot?.total)}</b></li>
            </ul>
            <p className="text-amber-700 text-xs">Esta acción no puede deshacerse.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={approving}>Cancelar</Button>
            <Button onClick={handleAprobar} disabled={approving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-aprobar-btn">
              {approving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revertConfirmOpen} onOpenChange={setRevertConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="revertir-confirm-dialog">
          <DialogHeader><DialogTitle>Pasar a borrador {formatPeriodo(periodo.periodo)}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>Al pasar la liquidación a borrador:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Se descartan los snapshots de liquidación aprobada.</li>
              <li>Se revierten los egresos automáticos de expensas.</li>
              <li>Los cobros <b>pendientes</b> generados se eliminan; los <b>pagos reales</b> ya cobrados se conservan.</li>
              <li>El período deja de ser visible en el Portal Propietario/Inquilino.</li>
            </ul>
            <p className="text-amber-700 text-xs">Podrás volver a editar y aprobar nuevamente.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertConfirmOpen(false)} disabled={reverting}>Cancelar</Button>
            <Button onClick={handleRevertir} disabled={reverting} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-revertir-btn">
              {reverting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Pasar a borrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Kpi = ({ label, value, highlight }) => (
  <div className={`bg-white border rounded-md px-3 py-2 ${highlight ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'}`}>
    <div className={`text-[10px] uppercase tracking-wide ${highlight ? 'text-emerald-700' : 'text-slate-500'}`}>{label}</div>
    <div className={`text-sm font-bold ${highlight ? 'text-emerald-900' : 'text-slate-900'}`}>{fmtMoney(value)}</div>
  </div>
);

const EmptyTabState = ({ label }) => (
  <div className="bg-white border border-dashed border-slate-200 rounded-xl p-10 text-center text-slate-500 text-sm">
    Sin datos de {label} para este período. Importá el Mayor desde la pestaña "Mayor" para empezar.
  </div>
);


const MesSelector = ({ mes, onMesChange, aprobado }) => {
  const shift = (delta) => {
    if (!mes || !onMesChange) return;
    const [y, m] = mes.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    onMesChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md flex items-center px-1.5 py-1.5 gap-1">
      <button onClick={() => shift(-1)}
              className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700"
              data-testid="mes-prev"
              title="Mes anterior">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div className="flex-1 text-center">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">Período</div>
        <div className="text-sm font-bold text-slate-900 inline-flex items-center gap-1">
          {formatPeriodo(mes)}
          {aprobado && <Lock className="w-3 h-3 text-emerald-600" />}
        </div>
      </div>
      <button onClick={() => shift(1)}
              className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700"
              data-testid="mes-next"
              title="Mes siguiente">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const TablaLiquidaciones = ({ comparativa }) => {
  if (comparativa === null || comparativa === undefined) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  const items = comparativa.items || [];
  const labelActual = comparativa?.mes_actual?.label || 'Mes actual';
  const labelAnterior = comparativa?.mes_anterior?.label || 'Mes anterior';

  const totales = items.reduce((acc, it) => ({
    total: acc.total + Number(it.total_actual || 0),
    total_ant: acc.total_ant + Number(it.total_anterior || 0),
  }), { total: 0, total_ant: 0 });
  // Variación por GASTO (valor absoluto): subió → +/rojo, bajó → -/verde.
  const gastoTot = Math.abs(totales.total);
  const gastoTotAnt = Math.abs(totales.total_ant);
  totales.var_monto = gastoTot - gastoTotAnt;
  totales.var_pct = gastoTotAnt !== 0 ? (totales.var_monto / gastoTotAnt) * 100 : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-220px)] overflow-y-auto">
      <table className="w-full text-sm" data-testid="tabla-liquidacion">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 sticky top-0 z-10">
          <tr>
            <th className="py-2 px-3 text-left font-medium">Unidad</th>
            <th className="py-2 px-3 text-right font-medium">Part. Exp</th>
            <th className="py-2 px-3 text-right font-medium">Total {labelActual}</th>
            <th className="py-2 px-3 text-right font-medium">{labelAnterior}</th>
            <th className="py-2 px-3 text-right font-medium">Var. monto</th>
            <th className="py-2 px-3 text-right font-medium">Var. %</th>
            <th className="py-2 px-2 text-center font-medium w-10"></th>
            <th className="py-2 px-3 text-left font-medium">Responsable</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.length === 0 && (
            <tr><td colSpan={8} className="py-8 text-center text-slate-400">Sin liquidaciones</td></tr>
          )}
          {items.map((it) => {
            const totAct = Number(it.total_actual || 0);
            const totAnt = Number(it.total_anterior || 0);
            const varMonto = it.variacion_monto ?? (Math.abs(totAct) - Math.abs(totAnt));
            const varPct = it.variacion_pct ?? (Math.abs(totAnt) !== 0 ? (varMonto / Math.abs(totAnt)) * 100 : null);
            return (
              <tr key={it.unidad_id}
                  className={`hover:bg-slate-50/60 ${it.es_subsuelo ? 'bg-indigo-50/40' : ''}`}
                  data-testid={`liq-fila-${it.unidad_id}`}>
                <td className="py-1 px-3 font-medium text-slate-900 whitespace-nowrap">
                  {it.unidad_nombre}
                  {it.n_unidades > 1 && (
                    <span className="ml-1.5 text-[10px] text-slate-400">({it.n_unidades})</span>
                  )}
                </td>
                <td className="py-1 px-3 text-right text-xs font-mono text-slate-500">{fmtPct(it.part_expensas)}</td>
                <td className="py-1 px-3 text-right font-mono font-semibold whitespace-nowrap">{fmtMoney(totAct)}</td>
                <td className="py-1 px-3 text-right font-mono text-slate-500 whitespace-nowrap">{fmtMoney(totAnt)}</td>
                <td className={`py-1 px-3 text-right font-mono whitespace-nowrap ${varClass(varMonto)}`}>{fmtVarMoney(varMonto)}</td>
                <td className={`py-1 px-3 text-right font-mono whitespace-nowrap ${varClass(varPct)}`}>{fmtVarPct(varPct)}</td>
                <td className="py-1 px-2 text-center"><Semaforo pct={varPct} /></td>
                <td className="py-1 px-3 text-xs whitespace-nowrap" data-testid={`liq-responsable-${it.unidad_id}`}>
                  <Responsable r={it.responsable} />
                </td>
              </tr>
            );
          })}
        </tbody>
        {items.length > 0 && (
          <tfoot className="bg-emerald-50 border-t-2 border-emerald-200 sticky bottom-0">
            <tr>
              <td className="py-2 px-3 font-semibold text-emerald-900" colSpan={2}>Total general</td>
              <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(totales.total)}</td>
              <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(totales.total_ant)}</td>
              <td className={`py-2 px-3 text-right font-mono font-bold whitespace-nowrap ${varClass(totales.var_monto)}`}>{fmtVarMoney(totales.var_monto)}</td>
              <td className={`py-2 px-3 text-right font-mono font-bold whitespace-nowrap ${varClass(totales.var_pct)}`}>{fmtVarPct(totales.var_pct)}</td>
              <td className="py-2 px-2 text-center"><Semaforo pct={totales.var_pct} /></td>
              <td></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

const Responsable = ({ r }) => {
  if (!r || !r.nombre) return <span className="text-slate-300">—</span>;
  const tipo = r.tipo || 'propietario';
  const config = {
    propietario: { icon: '🏠', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Propietario' },
    inquilino: { icon: '🧑‍💼', cls: 'text-blue-700 bg-blue-50 border-blue-200', label: 'Inquilino' },
    inquilino_fijo: { icon: '🏢', cls: 'text-violet-700 bg-violet-50 border-violet-200', label: 'Inquilino fijo' },
  }[tipo] || { icon: '•', cls: 'text-slate-700 bg-slate-50 border-slate-200', label: tipo };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] ${config.cls}`} title={`${config.label}${r.cuit ? ` · CUIT ${r.cuit}` : ''}${r.email ? ` · ${r.email}` : ''}`}>
      <span>{config.icon}</span>
      <span className="font-medium truncate max-w-[180px]">{r.nombre}</span>
    </span>
  );
};

const TablaApertura = ({ filas, filter = '' }) => {
  const q = filter.trim().toLowerCase();
  const filtered = !q ? filas : filas.filter((f) => (
    String(f.codigo || '').toLowerCase().includes(q) ||
    String(f.cuenta || '').toLowerCase().includes(q) ||
    String(f.rubro || '').toLowerCase().includes(q) ||
    String(f.subrubro || '').toLowerCase().includes(q) ||
    String(f.titulo || '').toLowerCase().includes(q)
  ));
  const tot = filtered.reduce((acc, f) => ({
    importe: acc.importe + Number(f.importe || 0),
    expensas: acc.expensas + Number(f.monto_expensas || 0),
    cocheras: acc.cocheras + Number(f.monto_cocheras || 0),
    locales: acc.locales + Number(f.monto_locales || 0),
    fondo_reserva: acc.fondo_reserva + Number(f.monto_fondo_reserva || 0),
  }), { importe: 0, expensas: 0, cocheras: 0, locales: 0, fondo_reserva: 0 });
  // Mostrar columna Locales solo si hay al menos un monto > 0 (Foresta).
  const showLocales = filas.some((f) => Number(f.monto_locales || 0) !== 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-220px)] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 sticky top-0">
          <tr>
            <th className="py-2 px-3 text-left font-medium">Código</th>
            <th className="py-2 px-3 text-left font-medium">Cuenta</th>
            <th className="py-2 px-3 text-left font-medium">Rubro</th>
            <th className="py-2 px-3 text-right font-medium">Importe</th>
            <th className="py-2 px-3 text-right font-medium">$ Expensas</th>
            <th className="py-2 px-3 text-right font-medium">$ Cocheras</th>
            {showLocales && <th className="py-2 px-3 text-right font-medium">$ Locales</th>}
            <th className="py-2 px-3 text-right font-medium">Fondo res.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <tr><td colSpan={showLocales ? 8 : 7} className="py-8 text-center text-slate-400">Sin resultados</td></tr>
          ) : filtered.map((f) => (
            <tr key={f.codigo} className="hover:bg-slate-50/60">
              <td className="py-1 px-3 font-mono text-xs text-slate-500">{f.codigo}</td>
              <td className="py-1 px-3 text-slate-900">{f.cuenta}</td>
              <td className="py-1 px-3 text-xs text-slate-500">{f.rubro}</td>
              <td className="py-1 px-3 text-right font-mono whitespace-nowrap">{fmtMoney(f.importe)}</td>
              <td className="py-1 px-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtMoney(f.monto_expensas)}</td>
              <td className="py-1 px-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtMoney(f.monto_cocheras)}</td>
              {showLocales && (
                <td className="py-1 px-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtMoney(f.monto_locales)}</td>
              )}
              <td className="py-1 px-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtMoney(f.monto_fondo_reserva)}</td>
            </tr>
          ))}
        </tbody>
        {filtered.length > 0 && (
          <tfoot className="bg-emerald-50 border-t-2 border-emerald-200 sticky bottom-0">
            <tr>
              <td className="py-2 px-3 font-semibold text-emerald-900" colSpan={3}>
                Total {q && `(${filtered.length} de ${filas.length})`}
              </td>
              <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.importe)}</td>
              <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.expensas)}</td>
              <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.cocheras)}</td>
              {showLocales && (
                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.locales)}</td>
              )}
              <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.fondo_reserva)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

const Semaforo = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return <span className="inline-block w-3 h-3 rounded-full bg-slate-300" title="Sin base anterior" />;
  }
  const abs = Math.abs(pct);
  let color, label;
  if (abs < 5) { color = 'bg-emerald-500'; label = 'Estable (<5%)'; }
  else if (abs <= 10) { color = 'bg-amber-400'; label = 'Atención (5-10%)'; }
  else { color = 'bg-red-500'; label = 'Crítico (>10%)'; }
  return <span className={`inline-block w-3 h-3 rounded-full ${color}`} title={label} />;
};

const fmtVarPct = (v) => {
  if (v === null || v === undefined) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
};

const fmtVarMoney = (v) => {
  if (v === null || v === undefined) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${fmtMoney(v)}`;
};

const varClass = (v) => {
  if (v === null || v === undefined || v === 0) return 'text-slate-500';
  return v > 0 ? 'text-red-600' : 'text-emerald-600';
};

// ── Resumen de gastos: color/semáforo por DIRECCIÓN (mejora/empeora/neutro) ──
const dirClass = (dir) =>
  dir === 'mejora' ? 'text-emerald-600' : dir === 'empeora' ? 'text-red-600' : 'text-slate-500';

const SemaforoDir = ({ dir }) => {
  if (!dir || dir === 'neutro') {
    return <span className="inline-block w-3 h-3 rounded-full bg-slate-300" title="Sin cambio / sin base" />;
  }
  const color = dir === 'mejora' ? 'bg-emerald-500' : 'bg-red-500';
  const label = dir === 'mejora' ? 'Mejora respecto al mes anterior' : 'Empeora respecto al mes anterior';
  return <span className={`inline-block w-3 h-3 rounded-full ${color}`} title={label} />;
};

const clasifTipoFila = (actual, anterior) => {
  const vals = [actual, anterior].filter((v) => v !== 0);
  if (!vals.length) return 'egreso';
  if (vals.every((v) => v > 0)) return 'ingreso';
  if (vals.every((v) => v < 0)) return 'egreso';
  return 'resultado';
};

const varPorTipo = (actual, anterior, tipo) => {
  if (tipo === 'egreso') {
    const ga = Math.abs(actual), gp = Math.abs(anterior);
    const vm = ga - gp;
    const vp = gp !== 0 ? (vm / gp) * 100 : null;
    const dir = ga < gp ? 'mejora' : ga > gp ? 'empeora' : 'neutro';
    return { vm, vp, dir };
  }
  const vm = actual - anterior;
  const vp = anterior !== 0 ? (vm / Math.abs(anterior)) * 100 : null;
  const dir = actual > anterior ? 'mejora' : actual < anterior ? 'empeora' : 'neutro';
  return { vm, vp, dir };
};

const CeldaComentario = ({ edificioId, periodoId, rubro, subrubro, initial, disabled }) => {
  const [val, setVal] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(initial || ''); }, [initial]);

  const save = async () => {
    if ((val || '') === (initial || '')) return;
    setSaving(true);
    try {
      await expensasApi.upsertResumenComentario(edificioId, periodoId, {
        rubro, subrubro, comentario: val,
      });
    } catch (e) {
      toast.error('No se pudo guardar el comentario');
      setVal(initial || '');
    } finally { setSaving(false); }
  };

  return (
    <input
      type="text"
      value={val}
      disabled={disabled || saving}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder={disabled ? '' : 'Agregar comentario...'}
      className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-indigo-300 focus:bg-white focus:outline-none rounded px-2 py-0.5 text-xs text-slate-700 placeholder-slate-300 disabled:opacity-60"
      data-testid={`comentario-${rubro}-${subrubro}`}
    />
  );
};

const TablaResumenGastos = ({ data, edificioId, periodoId, aprobado }) => {
  const [detalleOpen, setDetalleOpen] = useState(null); // { rubro, subrubro, importeResumen, mesLabel }
  if (data === null) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  const meses = data.meses || [];
  const filas = data.filas || [];
  const totales = data.totales || [0, 0];
  const varTotalMonto = data.variacion_total_monto ?? 0;
  const varTotalPct = data.variacion_total_pct;
  const varTotalDir = data.variacion_total_direccion;

  // Agrupar visualmente por rubro
  const gruposPorRubro = {};
  filas.forEach((f) => {
    if (!gruposPorRubro[f.rubro]) gruposPorRubro[f.rubro] = [];
    gruposPorRubro[f.rubro].push(f);
  });
  const rubros = Object.keys(gruposPorRubro);

  return (
    <>
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-220px)] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 sticky top-0">
          <tr>
            <th className="py-2 px-3 text-left font-medium">Rubro</th>
            <th className="py-2 px-3 text-left font-medium">Subrubro</th>
            {meses.map((m, i) => (
              <th key={m.periodo} className="py-2 px-3 text-right font-medium">
                {m.label}{i === 0 ? ' (actual)' : ''}
              </th>
            ))}
            <th className="py-2 px-3 text-right font-medium">Var. monto</th>
            <th className="py-2 px-3 text-right font-medium">Var. %</th>
            <th className="py-2 px-2 text-center font-medium w-10"></th>
            <th className="py-2 px-3 text-left font-medium">Comentario</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filas.length === 0 ? (
            <tr><td colSpan={6 + meses.length} className="py-8 text-center text-slate-400">Sin datos</td></tr>
          ) : rubros.map((rubro) => {
            const items = gruposPorRubro[rubro];
            const subTot = meses.map((_, i) => items.reduce((acc, it) => acc + (it.importes[i] || 0), 0));
            const tipos = items.map((f) => clasifTipoFila(f.importes[0], f.importes[1]));
            const hasIng = tipos.includes('ingreso');
            const hasEgr = tipos.includes('egreso');
            const subTipo = (hasIng && hasEgr) ? 'resultado' : (hasIng ? 'ingreso' : 'egreso');
            const { vm: subVarMonto, vp: subVarPct, dir: subDir } = varPorTipo(subTot[0], subTot[1], subTipo);
            return (
              <React.Fragment key={rubro}>
                {items.map((f, idx) => (
                  <tr key={`${rubro}-${f.subrubro}-${idx}`} className="hover:bg-slate-50/60">
                    <td className="py-1 px-3 text-slate-700">{idx === 0 ? <b>{rubro}</b> : ''}</td>
                    <td className="py-1 px-3 text-slate-600 text-xs">{f.subrubro}</td>
                    {f.importes.map((v, i) => (
                      <td
                        key={i}
                        className="py-1 px-3 text-right font-mono whitespace-nowrap cursor-pointer hover:bg-indigo-50/80 underline decoration-dotted decoration-slate-300 hover:decoration-indigo-500"
                        title={`Ver detalle de cuentas (Mayor) — ${meses[i]?.label || ''}`}
                        onClick={() => setDetalleOpen({
                          rubro: f.rubro,
                          subrubro: f.subrubro,
                          importeResumen: v,
                          mesLabel: meses[i]?.label,
                          periodo: meses[i]?.periodo,
                        })}
                        data-testid={`resumen-detalle-${f.rubro}-${f.subrubro}-${i}`}
                      >
                        {fmtMoney(v)}
                      </td>
                    ))}
                    <td className={`py-1 px-3 text-right font-mono whitespace-nowrap ${dirClass(f.direccion)}`}>{fmtVarMoney(f.variacion_monto)}</td>
                    <td className={`py-1 px-3 text-right font-mono whitespace-nowrap ${dirClass(f.direccion)}`}>{fmtVarPct(f.variacion_pct)}</td>
                    <td className="py-1 px-2 text-center"><SemaforoDir dir={f.direccion} /></td>
                    <td className="py-1 px-1 min-w-[200px]">
                      <CeldaComentario
                        edificioId={edificioId}
                        periodoId={periodoId}
                        rubro={f.rubro}
                        subrubro={f.subrubro}
                        initial={f.comentario}
                        disabled={aprobado}
                      />
                    </td>
                  </tr>
                ))}
                <tr className="bg-indigo-50 border-y border-indigo-200">
                  <td className="py-1.5 px-3 text-xs uppercase font-semibold text-indigo-900" colSpan={2}>Subtotal {rubro}</td>
                  {subTot.map((v, i) => (
                    <td key={i} className="py-1.5 px-3 text-right font-mono font-semibold text-indigo-900 whitespace-nowrap">{fmtMoney(v)}</td>
                  ))}
                  <td className={`py-1.5 px-3 text-right font-mono font-semibold whitespace-nowrap ${dirClass(subDir)}`}>{fmtVarMoney(subVarMonto)}</td>
                  <td className={`py-1.5 px-3 text-right font-mono font-semibold whitespace-nowrap ${dirClass(subDir)}`}>{fmtVarPct(subVarPct)}</td>
                  <td className="py-1.5 px-2 text-center bg-indigo-50"><SemaforoDir dir={subDir} /></td>
                  <td className="py-1.5 px-3 bg-indigo-50"></td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
        {filas.length > 0 && (
          <tfoot className="bg-emerald-50 border-t-2 border-emerald-200 sticky bottom-0">
            <tr>
              <td className="py-2 px-3 font-semibold text-emerald-900" colSpan={2}>Total general</td>
              {totales.map((v, i) => (
                <td key={i} className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(v)}</td>
              ))}
              <td className={`py-2 px-3 text-right font-mono font-bold whitespace-nowrap ${dirClass(varTotalDir)}`}>{fmtVarMoney(varTotalMonto)}</td>
              <td className={`py-2 px-3 text-right font-mono font-bold whitespace-nowrap ${dirClass(varTotalDir)}`}>{fmtVarPct(varTotalPct)}</td>
              <td className="py-2 px-2 text-center"><SemaforoDir dir={varTotalDir} /></td>
              <td className="py-2 px-3"></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    <DetalleSubrubroDialog
      isOpen={!!detalleOpen}
      onClose={() => setDetalleOpen(null)}
      edificioId={edificioId}
      periodoId={periodoId}
      rubro={detalleOpen?.rubro}
      subrubro={detalleOpen?.subrubro}
      mesLabel={detalleOpen?.mesLabel}
      periodo={detalleOpen?.periodo}
      importeResumen={detalleOpen?.importeResumen}
    />
    </>
  );
};
const esSinClasifIgnorableFn = (codigo) => {
  const c = String(codigo || '').trim();
  if (!c) return false;
  if (c === '440100') return true;
  return ['1', '2', '3'].includes(c[0]);
};

const TablaSinClasificar = ({ filas }) => {
  // Si el backend ya provee `estado`, lo usamos; si no, recalculamos.
  const conEstado = filas.map((f) => ({
    ...f,
    estado: f.estado || (esSinClasifIgnorableFn(f.codigo) ? 'permitida' : 'revisar'),
    cuenta: f.cuenta || 'Sin nombre',
  }));
  const permitidas = conEstado.filter((f) => f.estado === 'permitida');
  const problematicas = conEstado.filter((f) => f.estado === 'revisar');
  const tieneProblemas = problematicas.length > 0;

  const renderRow = (f, isProb) => (
    <tr key={f.codigo} className={isProb ? 'hover:bg-orange-50/40' : ''}>
      <td className={`py-1 px-3 font-mono text-xs ${isProb ? 'text-orange-900 font-semibold' : 'text-slate-500'}`}>{f.codigo}</td>
      <td className={`py-1 px-3 text-sm ${isProb ? 'text-orange-900' : 'text-slate-700'}`}>{f.cuenta}</td>
      <td className={`py-1 px-3 text-right font-mono ${isProb ? '' : 'text-slate-600'}`}>{fmtMoney(f.importe)}</td>
      <td className="py-1 px-3 text-center">
        {isProb ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 bg-orange-50 text-orange-700 ring-orange-200">Revisar</span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 bg-slate-100 text-slate-600 ring-slate-200">Permitida</span>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-3">
      {/* Contadores siempre visibles, en línea */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="sin-clasif-contadores">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 ring-1 ring-slate-200 text-slate-700">
          Permitidas: <b className="text-slate-900" data-testid="cnt-permitidas">{permitidas.length}</b>
        </span>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ring-1 ${tieneProblemas ? 'bg-orange-50 ring-orange-200 text-orange-800' : 'bg-emerald-50 ring-emerald-200 text-emerald-800'}`}>
          Revisar: <b data-testid="cnt-revisar">{problematicas.length}</b>
        </span>
      </div>

      {tieneProblemas ? (
        <div className="bg-orange-50 border border-orange-300 rounded-lg p-4" data-testid="alerta-sin-clasif-problematicos">
          <p className="text-sm text-orange-900 font-semibold mb-1">
            Hay cuentas de resultado sin clasificar que requieren revisión.
          </p>
          <p className="text-xs text-orange-800">
            Las cuentas que empiezan con 4 o 5 deben estar clasificadas, salvo la cuenta 440100.
            Agregalas al Plan de cuentas antes de aprobar el período.
          </p>
          <p className="text-xs text-orange-900 font-semibold mt-1">
            Problemas detectados: {problematicas.length} cuenta{problematicas.length !== 1 ? 's' : ''}.
          </p>
        </div>
      ) : (
        filas.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="info-sin-clasif-ok">
            <p className="text-sm text-emerald-900">
              No hay cuentas problemáticas sin clasificar. Las que aparecen abajo son permitidas (códigos 1, 2, 3 o 440100) y no bloquean la aprobación.
            </p>
          </div>
        )
      )}

      {problematicas.length > 0 && (
        <div className="bg-white border border-orange-200 rounded-lg overflow-hidden" data-testid="tabla-sin-clasif-problematicas">
          <div className="bg-orange-100 px-3 py-1.5 text-xs uppercase font-semibold text-orange-900">
            Requieren revisión ({problematicas.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2 px-3 text-left w-24">Código</th>
                  <th className="py-2 px-3 text-left">Nombre de cuenta</th>
                  <th className="py-2 px-3 text-right w-40">Importe</th>
                  <th className="py-2 px-3 text-center w-28">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {problematicas.map((f) => renderRow(f, true))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {permitidas.length > 0 && (
        <details className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden" data-testid="tabla-sin-clasif-permitidas" open={!tieneProblemas}>
          <summary className="cursor-pointer px-3 py-2 text-xs uppercase font-semibold text-slate-600 hover:bg-slate-100">
            Permitidas ({permitidas.length}) — códigos 1xxx / 2xxx / 3xxx · 440100
          </summary>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2 px-3 text-left w-24">Código</th>
                  <th className="py-2 px-3 text-left">Nombre de cuenta</th>
                  <th className="py-2 px-3 text-right w-40">Importe</th>
                  <th className="py-2 px-3 text-center w-28">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {permitidas.map((f) => renderRow(f, false))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {filas.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900">
          No hay cuentas sin clasificar.
        </div>
      )}
    </div>
  );
};

// ─── Vista operador (interna): tabla por unidad con desglose Mes / Cochera / Fee / Total ───
const TablaVistaOperador = ({ data, filter, onFilterChange }) => {
  if (data === null || data === undefined) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  const filas = data.filas || [];
  const q = String(filter || '').trim().toLowerCase();
  const filtradas = !q ? filas : filas.filter((r) => (
    String(r.grupo_nombre || r.unidad_nombre || '').toLowerCase().includes(q) ||
    String(r.responsable_nombre || '').toLowerCase().includes(q)
  ));
  const tot = filtradas.reduce((acc, r) => ({
    expensas_mes: acc.expensas_mes + Number(r.expensas_mes || 0),
    expensas_cochera: acc.expensas_cochera + Number(r.expensas_cochera || 0),
    fee: acc.fee + Number(r.fee_administracion_consorcio || 0),
    total: acc.total + Number(r.total_expensa || 0),
  }), { expensas_mes: 0, expensas_cochera: 0, fee: 0, total: 0 });

  return (
    <div className="space-y-2">
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-220px)] overflow-y-auto">
        <table className="w-full text-sm" data-testid="tabla-vista-operador">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 sticky top-0 z-10">
            <tr>
              <th className="py-2 px-3 text-left font-medium whitespace-nowrap">Unidad</th>
              <th className="py-2 px-3 text-left font-medium whitespace-nowrap">Propietario / Inquilino</th>
              <th className="py-2 px-3 text-right font-medium whitespace-nowrap">% Incidencia</th>
              <th className="py-2 px-3 text-right font-medium whitespace-nowrap">Expensas Mes</th>
              <th className="py-2 px-3 text-right font-medium whitespace-nowrap">Expensas cochera</th>
              <th className="py-2 px-3 text-right font-medium whitespace-nowrap">Fee admin. consorcio</th>
              <th className="py-2 px-3 text-right font-medium whitespace-nowrap">Total Expensa</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtradas.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-400">Sin resultados</td></tr>
            )}
            {filtradas.map((r) => (
              <tr key={r.key || r.unidad_id || r.grupo_id} className={`hover:bg-slate-50/60 ${r.huerfana_cochera ? 'bg-amber-50/60' : ''}`}>
                <td className="py-1 px-3 font-medium text-slate-800 whitespace-nowrap">
                  {r.grupo_nombre || r.unidad_nombre || '—'}
                  {r.n_unidades > 1 && (
                    <span className="ml-1.5 text-[10px] text-slate-400">({r.n_unidades})</span>
                  )}
                  {r.huerfana_cochera && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] uppercase bg-amber-100 text-amber-800 font-semibold" title="Cochera sin grupo asignado">
                      Cochera s/grupo
                    </span>
                  )}
                </td>
                <td className="py-1 px-3 text-slate-700">
                  {r.responsable_nombre || '—'}
                  {r.responsable_tipo === 'inquilino' && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] uppercase bg-indigo-50 text-indigo-700 font-semibold">Inquilino</span>
                  )}
                  {r.responsable_tipo === 'inquilino_fijo' && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] uppercase bg-violet-50 text-violet-700 font-semibold">Fijo</span>
                  )}
                </td>
                <td className="py-1 px-3 text-right font-mono text-xs text-slate-600 whitespace-nowrap">
                  {Number(r.part_expensas || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                </td>
                <td className="py-1 px-3 text-right font-mono whitespace-nowrap">{fmtMoney(r.expensas_mes)}</td>
                <td className="py-1 px-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtMoney(r.expensas_cochera)}</td>
                <td className="py-1 px-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtMoney(r.fee_administracion_consorcio)}</td>
                <td className="py-1 px-3 text-right font-mono font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(r.total_expensa)}</td>
              </tr>
            ))}
          </tbody>
          {filtradas.length > 0 && (
            <tfoot className="bg-emerald-50 border-t-2 border-emerald-200 sticky bottom-0">
              <tr>
                <td className="py-2 px-3 font-semibold text-emerald-900" colSpan={3}>
                  Total {q && `(${filtradas.length} de ${filas.length})`}
                </td>
                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.expensas_mes)}</td>
                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.expensas_cochera)}</td>
                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.fee)}</td>
                <td className="py-2 px-3 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{fmtMoney(tot.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

const TablaMovimientos = ({ movimientos, fileName, importedAt }) => {
  const [filter, setFilter] = useState('');
  if (movimientos === null) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  // Control de balance: ∑ Débito vs ∑ Crédito
  const totDebito = movimientos.reduce((s, m) => s + Number(m.debito || 0), 0);
  const totCredito = movimientos.reduce((s, m) => s + Number(m.credito || 0), 0);
  const diferencia = Number((totDebito - totCredito).toFixed(2));
  const balanceOk = Math.abs(diferencia) < 0.01;

  const filtered = !filter ? movimientos : movimientos.filter((m) => {
    const q = filter.toLowerCase();
    return (
      String(m.codigo || '').toLowerCase().includes(q) ||
      String(m.cuenta || '').toLowerCase().includes(q) ||
      String(m.detalle || '').toLowerCase().includes(q) ||
      String(m.subcuenta || '').toLowerCase().includes(q) ||
      String(m.nro_comp || '').toLowerCase().includes(q)
    );
  });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 mb-1.5">
        <div className="text-xs text-slate-600 flex flex-wrap items-center gap-3">
          <span>
            <FileSpreadsheet className="w-3.5 h-3.5 inline-block mr-1 text-slate-500" />
            <b>{fileName || '—'}</b>
            {importedAt && <span className="ml-2 text-slate-400">importado el {new Date(importedAt).toLocaleString('es-AR')}</span>}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${
              balanceOk
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-300 text-red-800'
            }`}
            data-testid="balance-control"
            title={
              balanceOk
                ? 'Débito y crédito están balanceados'
                : 'Las sumas de débito y crédito no coinciden — revisá el archivo'
            }
          >
            {balanceOk
              ? <CheckCircle2 className="w-3.5 h-3.5" />
              : <AlertTriangle className="w-3.5 h-3.5" />
            }
            <span>Débito {fmtMoney(totDebito)} · Crédito {fmtMoney(totCredito)}</span>
            <span className="font-semibold">
              {balanceOk
                ? '· Balanceado'
                : `· Diferencia ${fmtMoney(diferencia)}`}
            </span>
          </span>
        </div>
        <input type="text" placeholder="Buscar..." value={filter}
               onChange={(e) => setFilter(e.target.value)}
               className="text-sm px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 w-72" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto h-[calc(100vh-260px)] overflow-y-auto">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col className="w-[60px]" />
            <col className="w-[80px]" />
            <col className="w-[80px]" />
            <col className="w-[40px]" />
            <col className="w-[100px]" />
            <col className="w-[140px]" />
            <col />
            <col className="w-[105px]" />
            <col className="w-[105px]" />
            <col className="w-[105px]" />
            <col className="w-[115px]" />
          </colgroup>
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500 sticky top-0">
            <tr>
              <th className="py-1.5 px-2 text-left font-medium">Código</th>
              <th className="py-1.5 px-2 text-left font-medium">Cuenta</th>
              <th className="py-1.5 px-2 text-left font-medium">Fecha</th>
              <th className="py-1.5 px-2 text-left font-medium">Tipo</th>
              <th className="py-1.5 px-2 text-left font-medium">Nro. Comp</th>
              <th className="py-1.5 px-2 text-left font-medium">Subcuenta</th>
              <th className="py-1.5 px-2 text-left font-medium">Detalle</th>
              <th className="py-1.5 px-2 text-right font-medium">Débito</th>
              <th className="py-1.5 px-2 text-right font-medium">Crédito</th>
              <th className="py-1.5 px-2 text-right font-medium">Saldo</th>
              <th className="py-1.5 px-2 text-right font-medium bg-indigo-50 text-indigo-700">Importe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={11} className="py-8 text-center text-slate-400">Sin resultados</td></tr>
            ) : filtered.map((m, i) => (
              <tr key={i} className="hover:bg-slate-50/60">
                <td className="py-1 px-2 font-mono text-slate-500 truncate" title={m.codigo}>{m.codigo}</td>
                <td className="py-1 px-2 text-slate-700 truncate" title={m.cuenta}>{m.cuenta}</td>
                <td className="py-1 px-2 text-slate-500 whitespace-nowrap">{fmtFecha(m.fecha)}</td>
                <td className="py-1 px-2 text-slate-500">{m.tipo}</td>
                <td className="py-1 px-2 text-slate-500 whitespace-nowrap truncate" title={m.nro_comp}>{m.nro_comp}</td>
                <td className="py-1 px-2 text-slate-600 truncate" title={m.subcuenta}>{m.subcuenta}</td>
                <td className="py-1 px-2 text-slate-700 truncate" title={m.detalle}>{m.detalle}</td>
                <td className="py-1 px-2 text-right font-mono whitespace-nowrap">{m.debito ? fmtMoney(m.debito) : '—'}</td>
                <td className="py-1 px-2 text-right font-mono whitespace-nowrap">{m.credito ? fmtMoney(m.credito) : '—'}</td>
                <td className="py-1 px-2 text-right font-mono text-slate-500 whitespace-nowrap">{fmtMoney(m.saldo)}</td>
                <td className="py-1 px-2 text-right font-mono font-medium bg-indigo-50/40 text-indigo-900 whitespace-nowrap">{fmtMoney(m.importe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// DETALLE POR UNIDAD
// Vista por unidad funcional (no por propietario). Pensada para que
// cualquier persona lea rápido qué paga una unidad del período.
// ═══════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════
// DETALLE POR UNIDAD — diseño compacto, reporte profesional
// ═══════════════════════════════════════════════════════════════════

// Formato monetario en VALOR ABSOLUTO para presentación al usuario final.
// Internamente los importes pueden seguir con signo contable; este helper
// SOLO afecta la visualización en "Detalle por unidad" y sus modales.
const fmtAbs = (n) => fmtMoney(Math.abs(Number(n) || 0));

const tipoLabel = (t) => {
  const k = (t || '').toLowerCase();
  if (k === 'oficina') return 'Oficina';
  if (k === 'local') return 'Local';
  if (k === 'cochera') return 'Cochera';
  if (k === 'cowork') return 'Cowork';
  return t || '—';
};

// Fecha "YYYY-MM-DD" → "DD/MM/YYYY"
const fmtFecha = (s) => {
  if (!s) return '—';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// Recargo "0" / "0.0" → "0%" · "7.5" → "7,5%"
const fmtRecargo = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '0%';
  return v.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + '%';
};

const bloqueExpensasTitulo = (tipo) => {
  const k = (tipo || '').toLowerCase();
  if (k === 'oficina') return 'Expensas oficina';
  if (k === 'local') return 'Expensas local';
  if (k === 'cowork') return 'Expensas cowork';
  if (k === 'cochera') return 'Expensas unidad';
  return 'Expensas unidad';
};

const VistaPorUnidad = ({ edificioId, periodoId }) => {
  const [items, setItems] = useState([]);
  const [unidadKey, setUnidadKey] = useState('');
  const [vista, setVista] = useState(null);
  const [loadingItems, setLoadingItems] = useState(true);
  const [loadingVista, setLoadingVista] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDeuda, setDownloadingDeuda] = useState(false);
  const [openDetalleExpensas, setOpenDetalleExpensas] = useState(false);
  const [openDetalleCochera, setOpenDetalleCochera] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await expensasApi.getUnidadesSelector(edificioId, periodoId);
        setItems(r.data || []);
        if (r.data?.length) setUnidadKey(r.data[0].unidad_id_principal);
      } finally { setLoadingItems(false); }
    })();
  }, [edificioId, periodoId]);

  useEffect(() => {
    if (!unidadKey) return;
    setLoadingVista(true);
    setVista(null);
    (async () => {
      try {
        const r = await expensasApi.getVistaUnidad(edificioId, periodoId, unidadKey);
        setVista(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo cargar el detalle');
      } finally { setLoadingVista(false); }
    })();
  }, [edificioId, periodoId, unidadKey]);

  const handleDescargarPDF = async () => {
    if (!unidadKey) return;
    setDownloadingPdf(true);
    try {
      const r = await expensasApi.getVistaUnidadPDF(edificioId, periodoId, unidadKey);
      // Derivar filename del Content-Disposition (si viene) o armarlo
      const cd = r.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `expensas_unidad_${unidadKey}.pdf`;
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('PDF descargado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Descarga la "Constancia de estado de deuda" (libre deuda / con deuda según corresponda)
  const handleDescargarEstadoDeuda = async () => {
    if (!unidadKey) return;
    setDownloadingDeuda(true);
    try {
      const r = await expensasApi.getConstanciaDeudaPDF(edificioId, unidadKey);
      const cd = r.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `constancia_estado_deuda_${unidadKey}.pdf`;
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Constancia descargada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar la constancia');
    } finally {
      setDownloadingDeuda(false);
    }
  };

  if (loadingItems) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  if (items.length === 0) {
    return <div className="py-10 text-center text-sm text-slate-500">No hay unidades para listar.</div>;
  }

  const u = vista?.unidad;
  const exp = vista?.expensas_unidad;
  const coc = vista?.cochera_unidad;
  const vto = vista?.vencimientos || {};
  const tieneExpensas = exp && (Math.abs(exp.monto || 0) > 0.005 || Math.abs(exp.part_expensas || 0) > 0);
  const tieneCochera = !!coc && (coc.n_cocheras || 0) > 0;

  return (
    <div className="space-y-3">
      {/* Selector */}
      <div className="flex flex-wrap items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Ver unidad</label>
        <select
          value={unidadKey}
          onChange={(e) => setUnidadKey(e.target.value)}
          data-testid="vista-unidad-selector"
          className="text-sm h-8 px-2.5 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 min-w-[260px] font-medium"
        >
          {items.map((it) => (
            <option key={it.key} value={it.unidad_id_principal}>
              {it.label} · {tipoLabel(it.tipo_principal)}
              {it.n_cocheras > 0 ? ` · ${it.n_cocheras} cochera${it.n_cocheras > 1 ? 's' : ''}` : ''}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-600 whitespace-nowrap" data-testid="vista-unidad-propietario">
          Propietario: <span className="font-semibold text-slate-800">{vista?.unidad?.propietario_nombre || 'Sin asignar'}</span>
        </span>
        <span className="text-[11px] text-slate-400 ml-auto">{items.length} unidades</span>
        <button
          onClick={handleDescargarPDF}
          disabled={!vista || loadingVista || downloadingPdf}
          data-testid="btn-descargar-pdf"
          className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition"
        >
          {downloadingPdf ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generando…</>
          ) : (
            <>Descargar PDF</>
          )}
        </button>
        <button
          onClick={handleDescargarEstadoDeuda}
          disabled={!vista || loadingVista || downloadingDeuda}
          data-testid="btn-descargar-estado-deuda"
          className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-slate-900 bg-white border border-slate-300 hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed rounded-md transition"
          title="Descarga constancia de libre deuda o estado de deuda según corresponda"
        >
          {downloadingDeuda ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generando…</>
          ) : (
            <>Descargar estado de deuda</>
          )}
        </button>
      </div>

      {loadingVista ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : vista ? (
        <>
          {/* Tarjeta resumen compacta */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="vista-unidad-resumen">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100">
              {/* Datos de la unidad */}
              <div className="px-5 py-4 flex flex-col justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Unidad</div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-3xl font-bold text-slate-900 leading-none">{u?.label}</span>
                    <span className="text-sm text-slate-500">· {tipoLabel(u?.tipo_principal)}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-600">
                  <div><span className="text-slate-400">Período</span>  <span className="font-medium text-slate-700">{formatPeriodo(vista.periodo)}</span></div>
                  <div><span className="text-slate-400">Participación</span>  <span className="font-medium text-slate-700">{fmtPctV(u?.part_expensas || 0)}</span></div>
                  {tieneCochera && (
                    <div><span className="text-slate-400">Cocheras</span>  <span className="font-medium text-slate-700">{u.n_cocheras}</span></div>
                  )}
                </div>
                {(u?.propietario_nombre || u?.inquilino_nombre) && (
                  <div className="flex flex-col gap-0.5 text-xs text-slate-600 border-t border-slate-100 pt-2 mt-1" data-testid="responsables">
                    {u?.propietario_nombre && (
                      <div data-testid="resumen-propietario"><span className="text-slate-400">Propietario:</span>  <span className="font-medium text-slate-700">{u.propietario_nombre}</span></div>
                    )}
                    {u?.inquilino_nombre && (
                      <div data-testid="resumen-inquilino"><span className="text-slate-400">Inquilino:</span>  <span className="font-medium text-slate-700">{u.inquilino_nombre}</span></div>
                    )}
                  </div>
                )}
              </div>

              {/* Total a pagar */}
              <div className="px-5 py-4 bg-slate-50/70 md:min-w-[280px] flex flex-col justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total a pagar</div>
                  <div className="text-2xl sm:text-3xl font-bold font-mono text-slate-900 leading-tight" data-testid="vista-unidad-total">{fmtAbs(vista.total)}</div>
                </div>
                <div className="text-[11px] text-slate-500 space-y-0.5">
                  {tieneExpensas && (
                    <div className="flex items-center justify-between gap-3">
                      <span>{bloqueExpensasTitulo(u?.tipo_principal)}</span>
                      <span className="font-mono text-slate-700">{fmtAbs(exp.monto)}</span>
                    </div>
                  )}
                  {tieneCochera && (
                    <div className="flex items-center justify-between gap-3">
                      <span>Cochera</span>
                      <span className="font-mono text-slate-700">{fmtAbs(coc.monto)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Monto vigente hoy (mismo que Cobros) */}
            {vto.vigente && (
              <div className="border-t border-slate-200 bg-indigo-50/60 px-4 py-2 flex items-center justify-between gap-2" data-testid="monto-vigente-hoy">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-indigo-500 font-semibold">Monto a pagar hoy</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                    {vto.vigente.numero}° vto · {fmtFecha(vto.vigente.fecha)}{vto.vigente.recargo_pct > 0 ? ` · +${fmtRecargo(vto.vigente.recargo_pct)}` : ''}
                  </span>
                </div>
                <span className="text-lg font-bold font-mono text-indigo-900" data-testid="monto-vigente-valor">{fmtAbs(vto.vigente.monto)}</span>
              </div>
            )}

            {/* Vencimientos: fecha + recargo + monto */}
            <div className="grid grid-cols-3 divide-x divide-slate-200 border-t border-slate-200 bg-white" data-testid="vencimientos">
              {[
                { key: 'vto1', label: '1° Vencimiento', color: 'text-emerald-700', data: vto.vto1, num: 1 },
                { key: 'vto2', label: '2° Vencimiento', color: 'text-amber-700', data: vto.vto2, num: 2 },
                { key: 'vto3', label: '3° Vencimiento', color: 'text-red-700', data: vto.vto3, num: 3 },
              ].map((v) => (
                <div key={v.key} className={`px-4 py-2.5 ${vto.vigente?.numero === v.num ? 'ring-2 ring-inset ring-indigo-300 bg-indigo-50/40' : ''}`} data-testid={v.key}>
                  <div className="flex items-baseline justify-between gap-2 mb-0.5">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{v.label}</span>
                    <span className="text-[10px] text-slate-500 font-medium tabular-nums">{fmtFecha(v.data?.fecha)} · {fmtRecargo(v.data?.recargo_pct)}</span>
                  </div>
                  <div className={`text-base font-bold font-mono ${v.color}`}>{fmtAbs(v.data?.monto)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Bloques por concepto */}
          <div className={`grid grid-cols-1 ${tieneCochera ? 'md:grid-cols-2' : ''} gap-3`}>
            {tieneExpensas && (
              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="bloque-expensas">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{bloqueExpensasTitulo(u?.tipo_principal)}</h3>
                    <div className="text-2xl font-bold font-mono text-slate-900 leading-tight mt-0.5">{fmtAbs(exp.monto)}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Monto unidad</div>
                  </div>
                  <button
                    onClick={() => setOpenDetalleExpensas(true)}
                    className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline whitespace-nowrap"
                    data-testid="btn-ver-detalle-expensas"
                  >
                    Ver detalle →
                  </button>
                </div>
                <dl className="border-t border-slate-100 pt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Total edificio</dt>
                    <dd className="font-mono text-slate-700">{fmtAbs(exp.pool_edificio)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Participación</dt>
                    <dd className="font-mono text-slate-700">{fmtPctV(exp.part_expensas)}</dd>
                  </div>
                </dl>
              </div>
            )}

            {tieneCochera && (
              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="bloque-cochera">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Cochera</h3>
                    <div className="text-2xl font-bold font-mono text-slate-900 leading-tight mt-0.5">{fmtAbs(coc.monto)}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Monto unidad</div>
                  </div>
                  <button
                    onClick={() => setOpenDetalleCochera(true)}
                    className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline whitespace-nowrap"
                    data-testid="btn-ver-detalle-cochera"
                  >
                    Ver detalle →
                  </button>
                </div>
                <dl className="border-t border-slate-100 pt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Total gastos cochera</dt>
                    <dd className="font-mono text-slate-700">{fmtAbs(coc.total_gastos_cochera_bruto)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Total cocheras</dt>
                    <dd className="font-mono text-slate-700">{coc.cocheras_totales}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Valor por cochera</dt>
                    <dd className="font-mono text-slate-700">{fmtAbs(coc.cuota_por_cochera)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Cocheras de la unidad</dt>
                    <dd className="font-mono text-slate-700">{coc.n_cocheras}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>

          <DetalleExpensasUnidadDialog
            open={openDetalleExpensas}
            onOpenChange={setOpenDetalleExpensas}
            titulo={bloqueExpensasTitulo(u?.tipo_principal)}
            unidadLabel={u?.label}
            periodo={vista.periodo}
            partExpensas={exp?.part_expensas}
            detalles={vista.detalle_expensas || []}
            totalUnidad={exp?.monto}
          />
          <DetalleCocheraUnidadDialog
            open={openDetalleCochera}
            onOpenChange={setOpenDetalleCochera}
            unidadLabel={u?.label}
            periodo={vista.periodo}
            totalGastosCochera={coc?.total_gastos_cochera_bruto}
            totalACobrar={coc?.pool_a_cobrar}
            cuota={coc?.cuota_por_cochera}
            cocherasTotales={coc?.cocheras_totales}
            cocherasCobrables={coc?.cocheras_cobrables}
            nCocheras={coc?.n_cocheras}
            montoUnidad={coc?.monto}
            detalles={vista.detalle_cochera || []}
          />
        </>
      ) : null}
    </div>
  );
};

const DetalleExpensasUnidadDialog = ({ open, onOpenChange, titulo, unidadLabel, periodo, partExpensas, detalles, totalUnidad }) => {
  // Agrupar por rubro preservando el orden de aparición
  const grupos = [];
  const idx = new Map();
  (detalles || []).forEach((d) => {
    const r = d.rubro || 'Sin rubro';
    if (!idx.has(r)) { idx.set(r, grupos.length); grupos.push({ rubro: r, items: [] }); }
    grupos[idx.get(r)].items.push(d);
  });
  // Helper local: muestra signo real (positivos sin signo, negativos con "-")
  const fmtSigned = (n) => {
    const v = Math.round(Number(n || 0));
    const sign = v < 0 ? '-' : '';
    return `${sign}$ ${Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{titulo} · Unidad {unidadLabel}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            {formatPeriodo(periodo)} · Participación {fmtPctV(partExpensas || 0)}
          </p>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto -mx-6 px-6">
          <table className="w-full text-sm border-collapse" data-testid="detalle-expensas-tabla">
            <colgroup>
              <col />
              <col className="w-[160px]" />
              <col className="w-[150px]" />
            </colgroup>
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b sticky top-0 z-10">
              <tr>
                <th className="py-2 px-3 text-left font-semibold">Concepto</th>
                <th className="py-2 px-3 text-right font-semibold whitespace-nowrap">Edificio</th>
                <th className="py-2 px-3 text-right font-semibold whitespace-nowrap">Aporte unidad</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g, gi) => (
                <React.Fragment key={gi}>
                  <tr className="bg-slate-100/70 border-t border-slate-200">
                    <td colSpan={3} className="py-1.5 px-3 text-[11px] uppercase tracking-wider font-bold text-slate-700">
                      {g.rubro}
                    </td>
                  </tr>
                  {g.items.map((d, i) => {
                    const negEdif = Number(d.monto_edificio || 0) < 0;
                    const negApor = Number(d.aporte_unidad || 0) < 0;
                    return (
                      <tr key={i} className="hover:bg-slate-50/60 border-t border-slate-100">
                        <td className="py-1.5 px-3 pl-6 text-slate-700">{d.subrubro || '—'}</td>
                        <td className={`py-1.5 px-3 text-right font-mono tabular-nums whitespace-nowrap ${negEdif ? 'text-red-700' : 'text-slate-700'}`}>
                          {fmtSigned(d.monto_edificio)}
                        </td>
                        <td className={`py-1.5 px-3 text-right font-mono tabular-nums font-semibold whitespace-nowrap ${negApor ? 'text-red-700' : 'text-slate-900'}`}>
                          {fmtSigned(d.aporte_unidad)}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {grupos.length === 0 && (
                <tr><td colSpan={3} className="py-6 text-center text-slate-400">Sin datos</td></tr>
              )}
            </tbody>
            <tfoot className="border-t-2 border-slate-300 bg-slate-50 sticky bottom-0">
              <tr>
                <td className="py-2 px-3 text-right font-semibold text-slate-700 uppercase text-xs">Monto unidad</td>
                <td className="py-2 px-3"></td>
                <td className={`py-2 px-3 text-right font-mono tabular-nums font-bold whitespace-nowrap ${Number(totalUnidad || 0) < 0 ? 'text-red-700' : 'text-slate-900'}`}>
                  {fmtSigned(totalUnidad)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DetalleCocheraUnidadDialog = ({ open, onOpenChange, unidadLabel, periodo, totalGastosCochera, totalACobrar, cuota, cocherasTotales, cocherasCobrables, nCocheras, montoUnidad, detalles }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Cochera · Unidad {unidadLabel}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            {formatPeriodo(periodo)} · {nCocheras} cochera{nCocheras > 1 ? 's' : ''} de la unidad
          </p>
        </DialogHeader>
        <div className="space-y-3">
          {/* Cards superiores: ahora solo 3, sin redundancia con "Monto unidad" */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Total gastos cochera</div>
              <div className="font-mono text-sm font-semibold text-slate-800">{fmtAbs(totalGastosCochera)}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Total cocheras</div>
              <div className="font-mono text-sm font-semibold text-slate-800">{cocherasTotales}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Valor por cochera</div>
              <div className="font-mono text-sm font-semibold text-slate-800">{fmtAbs(cuota)}</div>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-sm" data-testid="resumen-cochera-unidad">
            Esta unidad tiene <span className="font-semibold text-slate-800">{nCocheras}</span> cochera{nCocheras > 1 ? 's' : ''}.
            {' '}Total cochera unidad: <span className="font-mono font-bold text-indigo-900">{fmtAbs(montoUnidad)}</span>.
          </div>

          <div className="max-h-[40vh] overflow-y-auto -mx-6 px-6">
            <table className="w-full text-sm" data-testid="detalle-cochera-tabla">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b sticky top-0">
                <tr>
                  <th className="py-2 px-3 text-left font-medium">Rubro</th>
                  <th className="py-2 px-3 text-left font-medium">Subrubro</th>
                  <th className="py-2 px-3 text-right font-medium">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(detalles || []).map((d, i) => (
                  <tr key={i} className="hover:bg-slate-50/60">
                    <td className="py-1.5 px-3 text-slate-700">{d.rubro}</td>
                    <td className="py-1.5 px-3 text-slate-500">{d.subrubro}</td>
                    <td className="py-1.5 px-3 text-right font-mono text-slate-700">{fmtAbs(d.monto_pool_27_27)}</td>
                  </tr>
                ))}
                {(detalles || []).length === 0 && (
                  <tr><td colSpan={3} className="py-6 text-center text-slate-400">Sin datos</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            El edificio tiene {cocherasTotales} cocheras. Los gastos se distribuyen
            según la regla definida y el valor por cochera resulta de esa distribución.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
